import { useEffect, useState } from "react";
import {
  GanttComponent,
  Inject,
  Selection,
  ColumnsDirective,
  ColumnDirective,
} from "@syncfusion/ej2-react-gantt";
import {
  dayWorkingTime,
  taskFields,
  tooltipSettings,
  labelSettings,
  splitterSettings,
  selectionSettings,
  taskbarTemplate,
  projectStartDate,
  connectorLineBackground,
} from "./GanttChartSettings";
import styles from "./GanttChart.module.css";
import {
  determineTimelineSettings,
  convertMinsToDays,
  getFormattedDurationFromMinutes,
} from "../../utils/timeUtils";

export const GanttChart = ({ ganttChartState, setSelectedSubtask }) => {
  const [ganttSubtasks, setGanttSubtasks] = useState(ganttChartState.subtasks);

  const handleRowSelected = args => {
    setSelectedSubtask(args.data.taskData);
  };

  const handleRowDeselected = args => {
    // Prevent deselection when streaming and data is changing
    // This attribute doesn't make sense but it turns out to be exactly what we need
    if (args.isInteracted) {
      return;
    }
    setSelectedSubtask(null);
  };

  const [timelineSettings, setTimelineSettings] = useState(() =>
    determineTimelineSettings(ganttChartState.subtasks),
  );
  const [projectEndDate, setProjectEndDate] = useState(
    new Date(projectStartDate),
  );

  // Combine both useEffects since they are always triggered together
  useEffect(() => {
    // Update gantt chart
    setGanttSubtasks(ganttChartState.subtasks);

    // Update timeline settings
    const { timelineSettings, minProjectDurationMins } =
      determineTimelineSettings(ganttChartState.subtasks);
    const MILLIS_PER_MINUTE = 60000;
    const BUFFER_RATIO = 2;
    const projectDurationMillis =
      BUFFER_RATIO *
      MILLIS_PER_MINUTE *
      Math.max(minProjectDurationMins, ganttChartState.totalDuration);
    setProjectEndDate(
      new Date(projectStartDate.getTime() + projectDurationMillis),
    );
    setTimelineSettings(timelineSettings);
  }, [ganttChartState.subtasks, ganttChartState.totalDuration]);

  return (
    <div className={styles.ganttChartContainer}>
      <GanttComponent
        dataSource={ganttSubtasks}
        taskFields={taskFields}
        tooltipSettings={tooltipSettings}
        labelSettings={labelSettings}
        timelineSettings={timelineSettings}
        splitterSettings={splitterSettings}
        selectionSettings={selectionSettings}
        taskbarTemplate={taskbarTemplate}
        projectStartDate={projectStartDate}
        projectEndDate={projectEndDate}
        autoCalculateDateScheduling={false}
        dayWorkingTime={dayWorkingTime}
        connectorLineBackground={connectorLineBackground}
        connectorLineWidth={1}
        rowSelected={handleRowSelected}
        rowDeselected={handleRowDeselected}
        height="100%"
      >
        <ColumnsDirective>
          <ColumnDirective
            field="formattedDuration"
            headerText="Duration"
            width="150"
            textAlign="Left"
          />
        </ColumnsDirective>
        <Inject services={[Selection]} />
      </GanttComponent>
    </div>
  );
};

export const getGanttChartSubtasks = subtasks => {
  return subtasks.map(subtask => {
    const startDate = new Date(
      projectStartDate.getTime() + subtask.startTime * 60000,
    );

    const endDate = new Date(startDate.getTime() + subtask.duration * 60000);

    return {
      subtaskID: subtask.subtaskID,
      name: subtask.subtaskTitle,
      startDate: startDate,
      duration: convertMinsToDays(subtask.duration),
      formattedDuration: getFormattedDurationFromMinutes(subtask.duration),
      endDate: endDate,
      progress: 0,
      dependencies: subtask.dependencies.map(dep => `${dep}FS`).join(","),
      isSync: subtask.isSync,
      description: subtask.description,
      workerID: subtask.workerID,
    };
  });
};
